import React from 'react';
import { Shield, BookOpen, Users, FileCheck, Link as LinkIcon, Server, Brain, Cpu, Sparkles } from 'lucide-react';
import HelpIconButton from '../HelpIconButton';
import type { TrustPortalScreenKey } from '../TrustPortalLayout';

// Landing page for the Trust Portal section. Provides a high-level map
// of the ten screens so reviewers can orient themselves and jump to any
// section. Body content is intentionally sparse — the Trust Portal is
// meant to be read section-by-section, not all on the landing page.

interface TrustPortalOverviewProps {
  onNavigate: (key: TrustPortalScreenKey) => void;
}

interface TileDefinition {
  key: TrustPortalScreenKey;
  label: string;
  description: string;
  icon: React.ElementType;
}

const TILES: TileDefinition[] = [
  {
    key: 'purpose',
    label: 'Purpose and Context',
    description: 'Why this portal exists and who it serves.',
    icon: BookOpen,
  },
  {
    key: 'scope',
    label: 'Sub-Provider Scope',
    description: 'Which upstream vendors are in scope.',
    icon: Users,
  },
  {
    key: 'security-posture',
    label: 'TSG Security Posture',
    description: 'SOC 2 roadmap, control families, evidence.',
    icon: Shield,
  },
  {
    key: 'quick-reference',
    label: 'Trust Portal Quick Reference',
    description: 'Independent verification URLs.',
    icon: LinkIcon,
  },
  {
    key: 'audit-reports',
    label: 'Requesting Full Audit Reports',
    description: 'Three pathways for obtaining full reports.',
    icon: FileCheck,
  },
  {
    key: 'railway',
    label: 'Vendor Profile — Railway',
    description: 'Production hosting provider.',
    icon: Server,
  },
  {
    key: 'anthropic',
    label: 'Vendor Profile — Anthropic',
    description: 'Primary LLM provider.',
    icon: Brain,
  },
  {
    key: 'openai',
    label: 'Vendor Profile — OpenAI',
    description: 'Secondary model provider.',
    icon: Cpu,
  },
  {
    key: 'mistral',
    label: 'Vendor Profile — Mistral (Placeholder)',
    description: 'Pending full onboarding.',
    icon: Sparkles,
  },
];

const TrustPortalOverview: React.FC<TrustPortalOverviewProps> = ({ onNavigate }) => {
  return (
    <div className="space-y-6">
      {/* Screen header — every Trust Portal screen has the help icon on
          the right of a page-style header so reviewers always know where
          to look for context. */}
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2.5 bg-blue-100 rounded-lg">
            <Shield className="w-6 h-6 text-blue-700" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Trust Portal</h1>
            <p className="text-sm text-gray-500 mt-0.5">
              A single-pane-of-glass view of TSG&apos;s security posture and sub-provider chain.
            </p>
          </div>
        </div>
        <HelpIconButton screenId="trust-portal-overview" showLabel />
      </div>

      {/* Welcome callout */}
      <div className="p-5 bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-100 rounded-xl">
        <h2 className="text-base font-semibold text-gray-900">Welcome</h2>
        <p className="mt-1.5 text-sm text-gray-700 leading-relaxed">
          This portal is built for client security reviewers, third-party risk managers, and
          new TSG consultants. Use the left-side navigation to explore each section, or click
          any tile below to jump straight in. First-time visitors should start with
          <button
            onClick={() => onNavigate('purpose')}
            className="mx-1 text-blue-700 hover:underline font-medium"
          >
            Purpose and Context
          </button>
          for grounding.
        </p>
      </div>

      {/* Section tiles */}
      <div>
        <h2 className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-3">
          Jump to a section
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {TILES.map(tile => {
            const Icon = tile.icon;
            return (
              <button
                key={tile.key}
                onClick={() => onNavigate(tile.key)}
                className="group text-left p-4 bg-white border border-gray-200 rounded-xl hover:border-blue-300 hover:shadow-sm transition-all"
              >
                <div className="flex items-start gap-3">
                  <div className="p-2 bg-gray-50 rounded-lg group-hover:bg-blue-50 transition-colors">
                    <Icon className="w-5 h-5 text-gray-600 group-hover:text-blue-700 transition-colors" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold text-gray-900">{tile.label}</div>
                    <div className="mt-0.5 text-xs text-gray-500 leading-relaxed">
                      {tile.description}
                    </div>
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Note on vendor profiles */}
      <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-900">
        <strong className="font-semibold">Note on vendor profiles:</strong> Railway, Anthropic,
        and OpenAI are live entries from the Jarvis Vendor Management module — opening their
        profile takes you to the same detail view used by the TSG risk team, so you always
        see current data. Mistral is a placeholder until onboarding is complete.
      </div>
    </div>
  );
};

export default TrustPortalOverview;
